/**
 * HostA Global - Forgot Password
 * Request a password reset link by email.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import BasicLayout from "layouts/authentication/components/BasicLayout";
import { authAPI } from "utils/api";

import logo from "assets/images/logo-ct.png";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    if (!email.trim()) {
      setMessage({ type: "error", text: "Please enter your email address." });
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(email.trim());
      setMessage({
        type: "success",
        text: res.message || "If an account exists with this email, you will receive a password reset link shortly. Check your inbox and spam folder.",
      });
      setEmail("");
    } catch (err) {
      setMessage({
        type: "error",
        text: err.message || "Something went wrong. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout>
      <Card
        sx={{
          overflow: "visible",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <MDBox
          borderRadius="lg"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={{
            background: "#b6925b",
            boxShadow: "none",
          }}
        >
          <MDBox
            component="img"
            src={logo}
            alt="HostA Global"
            sx={{
              width: "100%",
              maxWidth: 220,
              height: "auto",
              objectFit: "contain",
              borderRadius: "5px",
              mt: 1,
              mb: 1,
              mixBlendMode: "multiply",
              filter: "contrast(1.05)",
            }}
          />
          <MDTypography variant="h6" fontWeight="regular" sx={{ color: "#fff" }} mt={2}>
            Forgot password
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.9)" }}>
            Enter your email and we&apos;ll send you a reset link
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {message.text && (
              <MDBox mb={2}>
                <Alert severity={message.type}>{message.text}</Alert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  background: "#b99160",
                  color: "#fff",
                  "&:hover": { background: "#a67d4d" },
                  "&:disabled": { background: "#8a7358", color: "rgba(255,255,255,0.7)" },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  "Send reset link"
                )}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Remember your password?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  fontWeight="medium"
                  sx={{ color: "#b6925b", "&:hover": { textDecoration: "underline" } }}
                >
                  Sign in
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default ForgotPassword;
